import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { PublicNav } from "@/components/app/PublicNav";
import { Check, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, friendlyError } from "@/lib/api";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    tagline: "Try GlobalBuy AI with real sourcing — on us.",
    cta: "Get started",
    highlighted: false,
    lookup_key: null,
    features: [
      "5 sourcing requests / month",
      "Basic supplier discovery",
      "RFQ creation",
      "Basic quotation comparison",
      "AI guidance",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: "$19",
    cadence: "per month",
    tagline: "For small teams running regular sourcing.",
    cta: "Choose plan",
    highlighted: false,
    lookup_key: "gb_starter_monthly",
    features: [
      "25 sourcing requests / month",
      "Expanded supplier matching",
      "Advanced quotation comparison",
      "RFQ history",
      "Procurement analytics",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: "$49",
    cadence: "per month",
    tagline: "The sweet spot for growing procurement teams.",
    cta: "Choose plan",
    highlighted: true,
    lookup_key: "gb_professional_monthly",
    features: [
      "100 sourcing requests / month",
      "Advanced AI analysis",
      "Supplier risk insights",
      "Advanced analytics",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: "$149",
    cadence: "per month",
    tagline: "For international teams and higher-volume procurement.",
    cta: "Choose plan",
    highlighted: false,
    lookup_key: "gb_business_monthly",
    features: [
      "Higher usage with fair-use limits",
      "Team members",
      "Advanced procurement analytics",
      "Audit / activity history",
      "Business procurement controls",
      "Priority support",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loadingId, setLoadingId] = useState(null);

  const handlePaidCta = async (plan) => {
    if (loadingId) return;
    if (!user) {
      nav(`/signup?next=/pricing`);
      return;
    }
    setLoadingId(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoadingId(null);
      toast.error(friendlyError(e));
    }
  };

  return (
    <div className="bg-[hsl(var(--navy))] text-slate-100 min-h-screen">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="eyebrow mb-3">Pricing</div>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05] text-white">
            Simple pricing for serious<br/>
            <span className="text-[hsl(var(--gold))]">global procurement.</span>
          </h1>
          <p className="text-slate-400 mt-5 leading-relaxed md:text-lg">
            Start free. Upgrade when your sourcing volume grows. Secure checkout powered by Stripe.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-testid="pricing-grid">
          {PLANS.map((p) => {
            const isLoading = loadingId === p.id;
            return (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-lg border p-6 flex flex-col ${
                  p.highlighted
                    ? "border-[hsl(var(--gold))]/60 bg-[hsl(var(--gold))]/5 shadow-[0_0_0_1px_hsl(var(--gold)/0.3)]"
                    : "border-white/10 bg-[hsl(var(--navy-muted))]/70"
                }`}
              >
                {p.highlighted && (
                  <span className="absolute -top-3 right-6 pill bg-[hsl(var(--gold))] text-[hsl(var(--navy))] border-transparent" data-testid="most-popular-badge">
                    <Sparkles size={11}/> Most Popular
                  </span>
                )}
                <div>
                  <div className="text-sm text-slate-300 font-medium">{p.name}</div>
                  <div className="mt-4 flex items-baseline gap-1.5" data-selectable>
                    <span className="text-4xl font-semibold tracking-tight text-white">{p.price}</span>
                    <span className="text-xs text-slate-400">{p.cadence}</span>
                  </div>
                  <p className="text-[13px] text-slate-400 mt-3 leading-relaxed min-h-[42px]">{p.tagline}</p>
                </div>

                <ul className="mt-5 space-y-2.5 text-sm text-slate-300 flex-1" data-selectable>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={15} className={`mt-0.5 shrink-0 ${p.highlighted ? "text-[hsl(var(--gold))]" : "text-slate-400"}`}/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {p.id === "free" ? (
                    <Link
                      to={user ? `/${user.role}` : "/signup"}
                      data-testid={`pricing-cta-${p.id}`}
                      className={`${p.highlighted ? "btn-gold" : "btn-ghost-light"} w-full`}
                    >
                      {p.cta} <ArrowRight size={14}/>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handlePaidCta(p)}
                      disabled={isLoading}
                      data-testid={`pricing-cta-${p.id}`}
                      className={`${p.highlighted ? "btn-gold" : "btn-ghost-light"} w-full`}
                    >
                      {isLoading ? (<><Loader2 size={14} className="animate-spin"/> Redirecting…</>) : (<>{p.cta} <ArrowRight size={14}/></>)}
                    </button>
                  )}
                  <div className="text-[11px] text-slate-500 mt-3 text-center">
                    {p.id === "free" ? "No credit card required." : "Secure checkout · Test card 4242 4242 4242 4242"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-md border border-white/10 bg-[hsl(var(--navy-muted))]/60 p-5 max-w-3xl">
          <div className="text-sm text-slate-300 font-medium mb-1">About billing</div>
          <p className="text-[13px] text-slate-400 leading-relaxed">
            Checkout is handled securely by Stripe. In the current sandbox you can test with card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry and any CVC. Taxes are calculated by Stripe at checkout; you can cancel your subscription at any time.
          </p>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} GlobalBuy AI — International B2B sourcing</div>
          <Link to="/" className="text-slate-400 hover:text-white">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
